/* eslint-disable */
import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDirection } from '~/hooks/i18nHooks';
import { useServicesContext } from '~/controllers/ServicesContext';
import { Button, Col, Row, Spin, Typography } from 'antd';
import { CloseOutlined, ExclamationCircleOutlined, LoadingOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import ServiceDetailsModal from '~/components/ServiceDetailsModal';
import { useIsMobile } from '~/hooks/useScreenSizes';
import { useGetApplication } from '~/controllers/wizard';

interface Props extends ContainerProps {
  inputKey: string;
  checked?: boolean;
  disable?: boolean;
  hideCheckbox?: boolean;
  onClick?: any;
  onChange?: (e: boolean) => void;
  name: string;
  header: string;
  initialPage?: boolean;
  shortDescriptionText?: string;
  annualFeeText?: string | null;
  setupFeeText?: string | null;
  type?: string;
  imageUrl?: string;
  numberOfDevices?: any;
  setupFee?: any;
  annualFee?: any;
  longDescription?: string;
  feesAndCharges?: any;
  totalFee?: any;
  loading?: boolean;
  disabled?: boolean;
  /** For POS: maximum number of terminals allowed (0 = unlimited) */
  maxTerminals?: number;
  /** For POS: callback when terminal count changes */
  onTerminalCountChange?: (count: number) => void;
  /** For POS: current terminal count (controlled from parent) */
  terminalCount?: number;
}

const CustomCloseIcon = (props: any) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M6 18L18 6M6 6L18 18" stroke="#1C2027" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ServiceCheckbox: FC<Props> = (props) => {
  const {
    feesAndCharges,
    imageUrl,
    inputKey,
    checked,
    onClick,
    type,
    onChange,
    name,
    withBorderBottom = true,
    disable,
    hideCheckbox,
    numberOfDevices,
    totalFee,
    loading,
    shortDescriptionText,
    maxTerminals,
    onTerminalCountChange,
    terminalCount: terminalCountProp,
    setupFee,
    annualFee,
  } = props;

  const isPOS =
    type === 'pos' ||
    type === 'nGeniusPos' ||
    inputKey === 'POS' ||
    inputKey === 'nGeniusPos';

  // Promotion is visible until end-of-day 28 September 2026
  const PROMO_EXPIRY = new Date('2026-09-28T23:59:59');
  const isPromoActive = new Date() <= PROMO_EXPIRY;
  const [localTerminalCount, setLocalTerminalCount] = useState<number>(terminalCountProp ?? 1);

  useEffect(() => {
    if (terminalCountProp !== undefined) {
      setLocalTerminalCount(terminalCountProp);
    }
  }, [terminalCountProp]);

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = Math.max(1, localTerminalCount - 1);
    setLocalTerminalCount(next);
    onTerminalCountChange?.(next);
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    const max = maxTerminals && maxTerminals > 0 ? maxTerminals : Infinity;
    const next = Math.min(max as number, localTerminalCount + 1);
    setLocalTerminalCount(next);
    onTerminalCountChange?.(next);
  };

  const [productModalOpen, setProductModalOpen] = useState(false);
  const application = useGetApplication();
  const dir = useDirection();
  const servicesContext = useServicesContext();
  const { t, i18n } = useTranslation('ksaApplication');
  const isMobile = useIsMobile();

  const handleSelect = () => {
    if (onChange) {
      onChange(!checked);
    }
    setProductModalOpen(false); // Optionally close the modal after selection
  };

  const serviceBenefits = t(`${type}Benefits`)?.split('\n');
  return (
    <div>
      <Container
        isMobile={isMobile}
        className="ServiceCheckbox"
        checked={checked || disable}
        withBorderBottom={withBorderBottom}
        hideCheckbox={hideCheckbox}
        dir={dir}
        disable={disable}
        onClick={() => onClick && onClick(type)}
      >
        {/* Mobile only: ⓘ icon positioned absolute OUTSIDE the label so label clicks still toggle checkbox */}
        {isMobile && !loading && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              (window as any).dataLayer.push({
                event: 'event_productinfo',
                page_ln: i18n.language?.slice(0, 2) || 'en',
                page_name: window.location.href,
                page_source: document.referrer || 'direct',
                product_id: inputKey,
                product_name: name,
                user_id: localStorage.getItem('sob_temp_uid'),
              });
              setProductModalOpen(true);
            }}
            style={{
              position: 'absolute',
              top: 8,
              ...(dir === 'rtl' ? { left: 8 } : { right: 8 }),
              zIndex: 2,
            }}
            type="text"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M9.99984 10.8334V5.83341M10.4165 13.7501C10.4165 13.9802 10.23 14.1667 9.99984 14.1667C9.76972 14.1667 9.58317 13.9802 9.58317 13.7501M10.4165 13.7501C10.4165 13.52 10.23 13.3334 9.99984 13.3334C9.76972 13.3334 9.58317 13.52 9.58317 13.7501M10.4165 13.7501H9.58317M18.3332 10.0001C18.3332 14.6025 14.6022 18.3334 9.99984 18.3334C5.39746 18.3334 1.6665 14.6025 1.6665 10.0001C1.6665 5.39771 5.39746 1.66675 9.99984 1.66675C14.6022 1.66675 18.3332 5.39771 18.3332 10.0001Z" stroke="#1C2027" strokeWidth={1.25} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
            className="infoIconBtn"
          />
        )}
        <CheckboxLabel>
          <HiddenCheckbox
            type="checkbox"
            checked={checked}
            onClick={() => {
              // Only fire when the user is selecting (checking) a product, not deselecting
              if (!checked) {
                (window as any).dataLayer.push({
                  event: 'event_productselection',
                  page_ln: i18n.language?.slice(0, 2) || 'en',
                  page_name: window.location.href,
                  page_source: document.referrer || 'direct',
                  product_id: type,
                  product_name: name,
                  page_section: 'Products',
                  price_one_time: setupFee ?? 'Free',
                  price_annual: annualFee ?? 'Free',
                  user_id: localStorage.getItem('sob_temp_uid'),
                });
              }
            }}
            onChange={onChange}
            disabled={disable || servicesContext.disableServiceSelection}
          />
          <CardContentWrapper isMobile={isMobile}>
            <LeftSection isMobile={isMobile}>
              {isPOS && checked ? (
                /* POS selected: show −N+ counter in the left image column */
                <div
                  className="posTerminalCounter"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    size="small"
                    icon={<MinusOutlined />}
                    onClick={handleDecrement}
                    disabled={localTerminalCount <= 1}
                    className="counterBtn"
                  />
                  <span className="counterValue">{localTerminalCount}</span>
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={handleIncrement}
                    disabled={!!(maxTerminals && maxTerminals > 0 && localTerminalCount >= maxTerminals)}
                    className="counterBtn"
                  />
                </div>
              ) : numberOfDevices && checked && isMobile ? (
                <DeviceCenterBox>
                  {numberOfDevices}
                </DeviceCenterBox>
              ) : (
                <ImageWrapper>
                  <img
                    src={imageUrl}
                    alt="service-image"
                    className="ServiceCheckbox-ServiceImage"
                  />
                </ImageWrapper>
              )}
            </LeftSection>

            <ServiceCardInfo isMobile={isMobile}>
              <Typography.Paragraph className="service-header">{t(name)}</Typography.Paragraph>
              {!loading ? (
                <>
                  <Typography.Paragraph className="shortDescriptionText">
                    {shortDescriptionText ? t(shortDescriptionText) : t('Start accepting online payments via your website or mobile app.')}
                  </Typography.Paragraph>
                  {!isPOS && !isMobile && isPromoActive && (
                    <>
                      <Typography.Paragraph className="setupFeeText">
                        <Trans
                          t={t}
                          i18nKey="Zero Monthly Fees for the First 3 Months ,Valid until <strong>28 September 2026*</strong>"
                        >
                          Zero Monthly Fees for the First 3 Months ,Valid until <strong>28 September 2026*</strong>
                        </Trans>
                      </Typography.Paragraph>
                      <Typography.Paragraph className="setupFeeText">
                        {t('Setup One-Time Fee: Free')}
                      </Typography.Paragraph>
                    </>
                  )}
                  {/* Desktop only: View more details text link */}
                  {!isMobile && (
                    <Button
                      className="viewMoreDetails"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        (window as any).dataLayer.push({
                          event: 'event_productinfo',
                          page_ln: i18n.language?.slice(0, 2) || 'en',
                          page_name: window.location.href,
                          page_source: document.referrer || 'direct',
                          product_id: inputKey,
                          product_name: name,
                          user_id: localStorage.getItem('sob_temp_uid'),
                        });
                        setProductModalOpen(true);
                      }}
                      type="link"
                    >
                      {t('View more details')}
                    </Button>
                  )}
                </>
              ) : (
                <Spin indicator={<LoadingOutlined className="loadingSpinnerIcon" />} />
              )}
            </ServiceCardInfo>
          </CardContentWrapper>
        </CheckboxLabel>

        {productModalOpen && (
          <ServiceDetailsModal
            totalFee={totalFee}
            disable={disable}
            checked={checked}
            title={
              <ModalTitleRow dir={dir}>
                <ModalTitleText className="modalTitleText" dir={dir}>
                  {name === 'eCommerce' || name === 'E-commerce' || t(name) === 'E-commerce' || t(name) === 'E-Commerce' ? t('eCommerce_tab_label') : t(name)}
                </ModalTitleText>
                <CloseButton
                  type="text"
                  icon={<CustomCloseIcon />}
                  onClick={() => setProductModalOpen(false)}
                />
              </ModalTitleRow>
            }
            visible={productModalOpen}
            onCancel={() => setProductModalOpen(false)}
            footer={null}
            width={isMobile ? '92%' : 900}
            feesAndCharges={feesAndCharges}
            imgUrl={imageUrl}
            serviceBenefits={serviceBenefits}
            type={inputKey}
            inputType={type}
            inputName={name}
            handleSelect={handleSelect}
            closable={false} // Disable default close for custom handling
          />
        )}
      </Container>
    </div>
  );
};

interface ContainerProps {
  checked?: boolean;
  hideCheckbox?: boolean;
  withBorderBottom?: boolean;
  disable?: boolean;
  isMobile?: boolean;
}

const Container = styled.div<ContainerProps>`
  width: 100%;
  overflow: hidden;
  position: relative;
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  border: ${({ checked }) =>
    checked
      ? '2px solid #0069B1'
      : '1px solid #DADADA'};
  cursor: ${({ disable }) => (disable ? 'not-allowed' : 'pointer')};
  background-color: white;
  border-radius: ${({ isMobile }) => (isMobile ? '16px' : '8px')};
  padding: ${({ isMobile }) => (isMobile ? '0px' : '16px 20px')};

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    //background-color: rgba(231, 226, 226, 0.26); /* Gray overlay */
    display: ${({ disable }) => (disable ? 'block' : 'none')};
    z-index: 1;
    pointer-events: none;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  }

  .feesContainer {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: -30px;
  }

  .service-header {
    color: #000;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: 1.2;
    margin: 0 !important;
  }

  .shortDescriptionText {
    color: #4a4a4a;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 300;
    line-height: 16px; /* 133.333% */
    letter-spacing: 0.24px;
    margin: 0 !important;
  }

  .setupFeeText {
    color: #000;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 450;
    line-height: 20px; /* 166.667% */
    letter-spacing: 0.12px;
    margin-bottom: 0px;
  }

  .viewMoreDetails {
    color: #0069b1;
    padding: 0;
    margin: 0;
    margin-top: auto;
    padding-top: 12px;
    height: auto;
    width: fit-content;
    align-self: ${({ dir }) => (dir === 'rtl' ? '' : 'flex-start')};
    text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
  }

  .infoIconBtn {
    width: 25px;
    height: 25px;
    border-radius: 50%;
    // border: 1px solid #e0ecf5;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:hover {
      background: #f0f7ff;
      border-color: #0069b1;
      color: #0069b1;
    }
  }

  .transactionFeeText {
    color: #000;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 450;
    line-height: 20px; /* 166.667% */
    letter-spacing: 0.12px;
  }

  .ServiceCheckbox-ServiceImage {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .services-description {
    text-overflow: ellipsis;
    width: 80%;
    font-size: 12px;
    color: black;
  }

  .feesText {
    font-size: 12px;
  }

  /* Loading spinner inside service card */
  .loadingSpinnerIcon {
    font-size: 30px;
    margin: 20px;
  }

  .posTerminalCounter {
    display: flex;
    flex-direction: ${({ dir }) => (dir === 'rtl' ? 'row-reverse' : 'row')};
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 4px 0;

    @media (max-width: 576px) {
      gap: 6px;
    }

    .counterBtn {
      width: 32px !important;
      height: 32px !important;
      min-width: 32px !important;
      border-radius: 50% !important;
      border: 1.5px solid #2E84BF !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      padding: 0 !important;
      color: #2E84BF !important;
      background: #fff !important;
      box-shadow: none !important;

      @media (max-width: 576px) {
        width: 28px !important;
        height: 28px !important;
        min-width: 28px !important;
      }

      .anticon {
        font-size: 12px;
      }

      &:hover:not(:disabled) {
        border-color: #2E84BF !important;
        color: #2E84BF !important;
        background: #fff !important;
      }

      &:disabled {
        border-color: #dde8f0 !important;
        color: #c0cfd8 !important;
        cursor: not-allowed;
        background: #fff !important;
      }
    }

    .counterValue {
      min-width: 24px;
      text-align: center;
      font-size: 16px;
      font-weight: 500;
      color: #000;
      font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
      line-height: 1;
    }
  }
`;



const CheckboxLabel = styled.label``;

const HiddenCheckbox = styled.input<any>`
  display: none;
`;

const DeviceCenterBox = styled.div`
  min-height: 27vw;
  width: 100%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardContentWrapper = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: ${({ isMobile }) => (isMobile ? '16px' : '24px')};
  width: 100%;
`;

const LeftSection = styled.div<{ isMobile?: boolean }>`
  flex-shrink: 0;
  width: ${({ isMobile }) => (isMobile ? '110px' : '150px')};
  height: ${({ isMobile }) => (isMobile ? '90px' : '150px')};
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  border-radius: 8px;
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ServiceCardInfo = styled.div<{ isMobile?: boolean }>`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-grow: 1;
  height: ${({ isMobile }) => (isMobile ? '90px' : '150px')};
  padding: ${({ isMobile }) => (isMobile ? '12px 8px' : '5px 0px 0px 0px')};
`;

interface DirProp {
  dir?: string;
}

const ModalTitleRow = styled.div<DirProp>`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  // flex-direction: ${({ dir }) => (dir === 'rtl' ? 'row' : 'row')};
`;

const ModalTitleText = styled(Typography.Paragraph) <DirProp>`
  &.ant-typography, div.ant-typography {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal, sans-serif' : 'Inter, sans-serif')};
    font-size: 20px !important;
    font-weight: 500 !important;
    line-height: 36px !important;
    color: #000 !important;
    margin: 0 !important;
  }
`;

const CloseButton = styled(Button)`
  &.ant-btn, &.ant-btn-icon-only, &.ant-btn-lg, &.ant-btn-text {
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
    cursor: pointer !important;
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 24px !important;
    height: 24px !important;
    min-width: 24px !important;

    &:hover, &:focus, &:active {
      background: transparent !important;
      color: #1C2027 !important;
    }
  }
`;

export default ServiceCheckbox;

